import { Button, TextareaAutosize } from "@mui/material";
import { shade, tint } from "polished";
import { ChangeEvent, FC, KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { FaPaperclip, FaPaperPlane } from "react-icons/fa";
import styled from "styled-components";
import { sendMessage } from "../../../api/rooms/sendMessage";
import { FileType } from "../../../common/interfaces/IMessage";
import { useChat } from "../../../hooks/useChat";
import { useFullpageLoading } from "../../../hooks/useFullPageLoading";
import { useInfo } from "../../../hooks/useInfo";
import { useRemoteParticipant } from "../../../hooks/useRemoteParticipant";
import { useRoom } from "../../../hooks/useRoom";
import { useVideoChat } from "../../../hooks/useVideoChat";
import getTheme from "../../../styles/themes/getTheme";
import { showToast } from "../../../utils/showToast";
import handleFileName from "../../helpers/handleFileName";
import FileItem from "../FileItem/FileItem";
import useQuery from "../../../utils/useQuery";

const FormContainer = styled("form")`
  height: fit-content;
  display: flex;
  justify-content: center;
  position: relative;
  padding-top: 8px;
  margin-top: 5px;
  box-sizing: border-box;
  flex-flow: column;
  gap: 8px;
`;

const FilesWrapper = styled("div")<{ $itemHeight: number }>`
  max-height: calc(
    3 * ${(props) => props.$itemHeight}px
  ); // Calcula para exibir apenas 3 itens do tamanho passado
  overflow: auto;
  display: flex;
  flex-flow: column;
  box-shadow: inset 1px 2px 4px rgba(119, 103, 122, 0.51);
  border-radius: 4px;
`;

const InputsWrapper = styled("div")`
  display: flex;
  justify-content: center;
  &::before {
    content: "";
    display: block;
    position: absolute;
    height: 1px;
    width: 95%;
    top: 0;
    background-color: ${(props) => props.theme.colors.secondary};
  }
`;

const ActionButton = styled(Button)<{ $bgColor: string }>`
  && {
    border-radius: 0;
    width: 75px;
    min-width: fit-content;
    box-sizing: border-box;
    background-color: ${(props) => props.$bgColor};

    .MuiTouchRipple-child {
      background-color: ${(props) => tint(0.1, props.$bgColor)};
    }
    svg {
      fill: white;
    }
    &:hover {
      background-color: ${(props) => shade(0.3, props.$bgColor)};
    }
    &:last-of-type {
      border-radius: 0 0.25rem 0.25rem 0;
    }
  }
`;

type Form = {
  message: string;
};

const NewMessageForm: FC = () => {
  const { register, handleSubmit, reset } = useForm<Form>();
  const {
    addFiles,
    files,
    fileInputRef,
    concatMessages,
    messages,
    loadMessages,
    clearFiles,
  } = useChat();
  const { room } = useRoom();
  const { remoteParticipant } = useRemoteParticipant();
  const { telemedicineData } = useInfo();
  const { permission } = useVideoChat();
  const { handleShowFullPageLoading } = useFullpageLoading();
  const { colors } = getTheme(
    useQuery().get("theme") || ""
  );

  // Envia mensagem
  const onSubmit = (data: Form) => {
    const messageTxt = data.message.trim();
    if (messageTxt.length === 0 && files.length === 0) return;

    if (room) {
        const [localDataTrackPublication] = [
            ...Array.from(room.localParticipant.dataTracks.values()),
        ];

        const formData = new FormData();
        formData.append("text", messageTxt);

        for (const file of files) {
            formData.append("files[]", file);
        }

        if (files.length > 0) {
            handleShowFullPageLoading(true);
        }

        sendMessage(formData)
            .then((response) => {
                handleShowFullPageLoading(false);
                localDataTrackPublication.track.send("new-message");
                console.log(response)
                if (response.success && response.data) { 
                    concatMessages([response.data]);
                    loadMessages(false);
                }
            })
            .catch(() => {
                handleShowFullPageLoading(false);
            });

        clearFiles();
    }

    reset();
  };




  // Função para enviar mensagem quando apertar a tecla ENTER
  const handleKeyPress = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    if (ev.key === "Enter" && !ev.ctrlKey && !ev.shiftKey) {
      ev.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  // Função para dar trigger no input do tipo file, e exibir para o usuário escolher quais arquivos quer enviar
  const handleAppendFiles = () => {
    if (fileInputRef && fileInputRef.current) {
      if (files.length <= 3) {
        fileInputRef.current.click();
      } else {
        showToast("info", {
          text: "É permitido enviar apenas 3 arquivos.",
          timer: 3000,
        });
      }
    }
  };

  const handleFileSelect = ({
    currentTarget,
  }: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = currentTarget.files;

    if (selectedFiles) addFiles(selectedFiles);
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      {files.length > 0 && (
        <FilesWrapper $itemHeight={32}>
          {files.map((file, index) => {
            const { formattedName, extension } = handleFileName(file.name);
            return (
              <FileItem
                key={index}
                index={index}
                name={formattedName}
                extension={extension}
                height={32}
              />
            );
          })}
        </FilesWrapper>
      )}
      <InputsWrapper>
        <TextareaAutosize
          placeholder="Digite aqui..."
          {...register("message")}
          minRows={1}
          maxRows={7}
          onKeyPress={handleKeyPress}
          autoFocus
          style={{
            width: "100%",
            fontFamily: '"Quicksand", sans-serif',
            fontSize: "1rem",
            lineHeight: "1.5",
            resize: "none",
            outline: "none",

            padding: "0.5rem 0.75rem",
            borderRadius: "0.25rem 0 0 0.25rem",
            border: "1px solid #cfcfcf",
          }}
        />
        <ActionButton $bgColor={colors.primary} type="submit">
          <FaPaperPlane size={20} />
        </ActionButton>
        <ActionButton $bgColor={"#2a3142"} onClick={() => handleAppendFiles()}>
          <FaPaperclip size={20} />
        </ActionButton>
      </InputsWrapper>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png, image/jpg, image/tif, image/bmp, application/pdf"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        multiple
      />
    </FormContainer>
  );
};

export default NewMessageForm;
